import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Eye, Plus } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MerchantTableSkeleton } from "@/components/merchant/MerchantTableSkeleton";
import { toast } from "sonner";
import { ROLE_CODES } from "@/constants";
import { MerchantApi } from "@/api";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { getFormattedDate, getFormattedDateTime } from "@/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Filter, Search, RotateCcw } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";

import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";
import { useDebounce } from "@/hooks/use-debounce";

const PAGE_SIZE = 10;

const Merchants = ({ basePath = "/accesspoint" }: { basePath?: string }) => {
  const primaryBtnClass =
    basePath === "/offers"
      ? "bg-rose-600 hover:bg-rose-700"
      : "bg-blue-600 hover:bg-blue-700";
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const [merchants, setMerchants] = useState([]);
  const [totalNumberOfMerchants, setTotalNumberOfMerchants] = useState(0);
  const [filters, setFilters] = useState(
    () =>
      location.state?.filters || {
        country: "",
        merchantName: "",
        merchantCode: "",
        merchantType: "",
        programId: "",
      }
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [merchantToDelete, setMerchantToDelete] = useState<any | null>(null);

  const [programs, setPrograms] = useState([]);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const [activeCategory, setActiveCategory] = useState("program");
  const [pendingFilterSelections, setPendingFilterSelections] = useState({
    program: filters.programId || "",
    type: filters.merchantType || "",
  });
  const [searchInputs, setSearchInputs] = useState({ program: "" });
  const [programDraft, setProgramDraft] = useState(
    pendingFilterSelections.program
  );

  const getMerchants = async (page?) => {
    setIsLoading(true);

    console.log("filters", filters);

    let queryParams = {};
    if (userInfo?.roleCode === ROLE_CODES.MERCHANT_ADMIN) {
      queryParams = {
        country: filters?.country || null,
        merchantType: filters?.merchantType || null,
        merchantName: filters?.merchantName?.trim() || null,
        merchantId: userInfo?.merchantId,
      };
    } else {
      queryParams = {
        country: filters?.country || null,
        merchantType: filters?.merchantType || null,
        merchantName: filters?.merchantName?.trim() || null,
        merchantId: userInfo?.merchantId,
        programId: filters?.programId,
      };
    }

    try {
      const response =
        basePath === "/offers"
          ? await MerchantApi.getMerchantsOfOfferFromDB({
              country: filters?.country || null,
              merchantName: filters?.merchantName?.trim() || "",
              merchantCode: filters?.merchantCode?.trim() || "",
              programId: filters?.programId || "",
              pageNumber: page ?? currentPage,
              pageSize: rowsPerPage,
              sortOrder: "asc",
              sortBy: "createdDate",
            })
          : await MerchantApi.getMerchantsFromDB({
              ...queryParams,
              pageNumber: page || currentPage,
              pageSize: rowsPerPage,
              sortOrder: "desc",
              sortBy: "createdDate",
            });
      const merchantList =
        basePath === "/offers"
          ? response?.data?.merchantOfOfferVOList || []
          : response?.data?.merchantVOList || [];

      const merchants = merchantList?.map(
        (merchant, index) => {
          return {
            id: merchant?.id,
            name: merchant?.merchantName,
            merchantCode: merchant?.merchantCode || "",
            businessLocation: merchant?.businessLocation || "",
            businessType:
              merchant?.businessType === "ONLINE" ? "Online" : "Offline",
            city: merchant?.city || "",
            country: merchant?.country || "",
            currency: merchant?.currencyVO?.iso3CurrencyCode || "",
            merchantImageUri: merchant?.merchantImageUri || "",
            created: merchant?.createdDateInMillis
              ? getFormattedDateTime(merchant?.createdDateInMillis)
              : "",
            originalMerchantObject: merchant,
          };
        }
      );
      setMerchants(merchants);
      setTotalNumberOfMerchants(response?.data?.totalNumberOfMerchants);
    } catch (error) {
      toast.error("Failed to fetch merchants");
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(totalNumberOfMerchants / rowsPerPage);

  // Get paginated data
  // const merchants = allMerchants.slice(
  //   (currentPage - 1) * PAGE_SIZE,
  //   currentPage * PAGE_SIZE
  // );

  const debouncedMerchantName = useDebounce(filters.merchantName, 500);

  useEffect(() => {
    getMerchants(currentPage);
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    if (basePath === "/offers") {
      setPrograms([]);
      return;
    }
    // Fetch programs for the filter dropdown
    async function fetchPrograms() {
      try {
        const res = await import("@/api/program.api").then((m) =>
          m.ProgramApi.getProgramsFromDB()
        );
        setPrograms(res?.data?.programVOs || []);
      } catch (e) {
        setPrograms([]);
      }
    }
    fetchPrograms();
  }, [basePath]);

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, merchantName: e.target.value }));
    setCurrentPage(0);
  };

  const handleOptionSelect = (category, value) => {
    setPendingFilterSelections((prev) => ({
      ...prev,
      [category]: category === "type" && value ? value.toUpperCase() : value,
    }));
  };
  const handleSearchInput = (category, value) => {
    setSearchInputs((prev) => ({ ...prev, [category]: value }));
  };
  const handleClearAll = () => {
    setPendingFilterSelections({ program: "", type: "" });
    setSearchInputs({ program: "" });
    setFilters((prev) => ({
      ...prev,
      programId: "",
      merchantType: "",
      merchantName: "",
    }));
    setCurrentPage(0);
    setFilterPopoverOpen(false);
  };
  const handleProgramApply = () => {
    setPendingFilterSelections((prev) => ({ ...prev, program: programDraft }));
  };

  const applyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      programId:
        pendingFilterSelections.program === "all"
          ? ""
          : pendingFilterSelections.program,
      merchantType: pendingFilterSelections.type,
    }));
    setCurrentPage(0);
    setFilterPopoverOpen(false);
  };

  useEffect(() => {
    getMerchants(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedMerchantName,
    filters.country,
    filters.merchantType,
    filters.programId,
    currentPage,
    rowsPerPage,
  ]);

  const renderOptions = () => {
    switch (activeCategory) {
      case "program":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            <input
              placeholder="Search program"
              value={searchInputs.program}
              onChange={(e) => handleSearchInput("program", e.target.value)}
              className="h-7 w-full text-xs border rounded px-2 mb-2"
            />
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="program-filter"
                checked={
                  pendingFilterSelections.program === "all" ||
                  pendingFilterSelections.program === ""
                }
                onChange={() => handleOptionSelect("program", "all")}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="text-xs font-medium">All Programs</span>
            </label>
            {programs
              .filter((p) =>
                p.programName
                  .toLowerCase()
                  .includes((searchInputs.program || "").toLowerCase())
              )
              .map((program) => (
                <label
                  key={program.id}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="program-filter"
                    checked={
                      pendingFilterSelections.program === program.id?.toString()
                    }
                    onChange={() =>
                      handleOptionSelect("program", program.id?.toString())
                    }
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="text-xs font-medium">
                    {program.programName}
                  </span>
                </label>
              ))}
          </div>
        );
      case "type":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="type-filter"
                checked={pendingFilterSelections.type === ""}
                onChange={() => handleOptionSelect("type", "")}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="text-xs font-medium">All Types</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="type-filter"
                checked={pendingFilterSelections.type === "ONLINE"}
                onChange={() => handleOptionSelect("type", "ONLINE")}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="text-xs font-medium">Online</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="type-filter"
                checked={pendingFilterSelections.type === "OFFLINE"}
                onChange={() => handleOptionSelect("type", "OFFLINE")}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="text-xs font-medium">Offline</span>
            </label>
          </div>
        );
      default:
        return null;
    }
  };

  // Calculate active filter count
  const activeFilterCount = [
    pendingFilterSelections.program,
    pendingFilterSelections.type,
  ].filter(Boolean).length;

  const offersMerchantColumns = [
    {
      header: "Created Date",
      accessorKey: "created",
      cell: (row) => row.created || "-",
    },
    {
      header: "Logo",
      accessorKey: "merchantImageUri",
      cell: (row) => (
        <div className="flex items-center">
          <img
            src={row.merchantImageUri ? row.merchantImageUri : "/placeholder.svg"}
            alt={row.name}
            className="h-10 w-10 object-contain rounded-md"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
        </div>
      ),
    },
    {
      header: "Code",
      accessorKey: "merchantCode",
    },
    {
      header: "Name",
      accessorKey: "name",
    },
    {
      header: "Business Location",
      accessorKey: "businessLocation",
      cell: (row) => row.businessLocation || "-",
    },
    {
      header: "City",
      accessorKey: "city",
    },
    {
      header: "Country",
      accessorKey: "country",
    },
    {
      header: "Actions",
      id: "actions",
      cell: (row) => (
        <Button variant="ghost" size="sm" className="h-8 gap-1" asChild>
          <Link
            to={`${basePath}/merchants/${row.id}`}
            state={{ merchant: row.originalMerchantObject }}
          >
            <Eye className="h-4 w-4" />
            View
          </Link>
        </Button>
      ),
    },
  ];

  const defaultMerchantColumns = [
    {
      header: "Created Date",
      accessorKey: "created",
    },
    {
      header: "Logo",
      accessorKey: "merchantImageUri",
      cell: (row) => (
        <div className="flex items-center">
          <img
            src={
              row.merchantImageUri
                ? row.merchantImageUri
                : "/placeholder.svg"
            }
            alt={row.name}
            className="h-10 w-10 object-contain rounded-md"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
        </div>
      ),
    },
    {
      header: "Name",
      accessorKey: "name",
    },
    {
      header: "Offline/Online",
      accessorKey: "businessType",
    },
    {
      header: "City / Country",
      id: "cityCountry",
      cell: (row) => (
        <div>
          <div>{row.city}</div>
          <div className="text-xs text-gray-500 mt-0.5">{row.country}</div>
        </div>
      ),
    },
    {
      header: "Currency",
      accessorKey: "currency",
    },

    {
      header: "Actions",
      id: "actions",
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white">
            <PermissionGuard permissionCode={PERMISSION_CODES.VIEW_MERCHANT}>
              <DropdownMenuItem asChild>
                <Link
                  to={`${basePath}/merchants/${row.id}`}
                  state={{ merchant: row.originalMerchantObject }}
                >
                  View Details
                </Link>
              </DropdownMenuItem>
            </PermissionGuard>

            <PermissionGuard permissionCode={PERMISSION_CODES.DELETE_MERCHANT}>
              <DropdownMenuItem
                onClick={() => {
                  setMerchantToDelete(row);
                  setDeleteDialogOpen(true);
                }}
                className="text-red-600"
              >
                Delete Merchant
              </DropdownMenuItem>
            </PermissionGuard>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const merchantColumns =
    basePath === "/offers" ? offersMerchantColumns : defaultMerchantColumns;

  return (
    <div className="space-y-8">
      {/* Heading and Add Merchant button */}
      <div className="flex items-center justify-between">
        <div>
          {basePath === "/offers" && (
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">
              Offer Point
            </p>
          )}
          <h1 className="text-2xl font-bold text-gray-800">Merchants</h1>
          <p className="text-gray-500 mt-1">
            Manage your merchants, their offers, and store locations
          </p>
        </div>
        {basePath === "/offers" ? (
          <Button className={primaryBtnClass} asChild>
            <Link to={`${basePath}/merchants/create`}>
              <Plus className="h-4 w-4 mr-2" /> Add Merchant
            </Link>
          </Button>
        ) : (
          <PermissionGuard permissionCode={PERMISSION_CODES.CREATE_MERCHANT}>
            <Button className={primaryBtnClass} asChild>
              <Link to={`${basePath}/merchants/create`}>
                <Plus className="h-4 w-4 mr-2" /> Add Merchant
              </Link>
            </Button>
          </PermissionGuard>
        )}
      </div>

      {/* Filter section */}
      <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex-1 flex gap-2 items-end">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                id="search"
                placeholder="Search by merchant name..."
                className="pl-9"
                value={filters.merchantName}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant={activeFilterCount > 0 ? "default" : "outline"}
                className="flex items-center gap-2 px-3 py-2 text-sm"
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="end">
              <div className="flex max-h-[320px] h-[320px]">
                <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                  {[
                    { label: "Program", value: "program" },
                    { label: "Type", value: "type" },
                  ].map((cat) => (
                    <button
                      key={cat.value}
                      className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                        activeCategory === cat.value
                          ? "bg-white shadow text-blue-600"
                          : "text-gray-700"
                      }`}
                      onClick={() => setActiveCategory(cat.value)}
                    >
                      {cat.label}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <button
                    className="text-[10px] text-gray-500 hover:underline mt-2"
                    onClick={handleClearAll}
                    type="button"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex-1 p-2 flex flex-col min-h-0">
                  <div className="text-xs font-medium flex-1 min-h-0 overflow-y-auto gap-1 flex flex-col">
                    {renderOptions()}
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      className="text-xs px-3 py-1 h-7"
                      onClick={applyFilters}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2 px-3 py-2 text-sm"
            onClick={handleClearAll}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>
      </div>

      {/* Merchants Table */}
      <DataTable
        title="All Merchants"
        data={isLoading ? [] : merchants}
        columns={merchantColumns}
        customTableBody={isLoading ? <MerchantTableSkeleton /> : undefined}
      />

      {/* Pagination controls */}
      {!isLoading && totalNumberOfMerchants > 0 && (
        <div className="mt-4 w-full">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Rows per page:</span>
              <Select
                value={rowsPerPage.toString()}
                onValueChange={(value) => {
                  setRowsPerPage(Number(value));
                  setCurrentPage(0);
                }}
              >
                <SelectTrigger className="w-[80px]">
                  <SelectValue placeholder={rowsPerPage.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <EnhancedPaginationControl
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
            <div className="w-[160px]" /> {/* Spacer to balance layout */}
          </div>
        </div>
      )}

      {/* Delete Merchant Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Merchant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the merchant "
              {merchantToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                // TODO: Implement actual delete logic here
                // deleteMerchant(merchantToDelete?.id);
                setDeleteDialogOpen(false);
                const deletedName = merchantToDelete?.name || "";
                setMerchantToDelete(null);
                toast.success(`Merchant ${deletedName} deleted`);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Merchants;
